import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { CARD } from '@/modules/supply/components/LoadEstimateParts';
import { LoadRestrictionPanel } from '@/modules/supply/components/LoadRestrictionPanel';
import { SupplyLoadVisualization } from '@/modules/supply/components/SupplyLoadVisualization';
import type { LoadEstimate } from '@/modules/supply/hooks/useLoadEstimate';
import type { LoadEstimateTruck as LoadEstimateTruckState } from '@/modules/supply/hooks/useLoadEstimateTruck';
import type { LoadEstimateSupplierResult } from '@/modules/supply/utils/loadEstimate';
import { LOAD_ESTIMATE_KEY as T, num } from '@/modules/supply/utils/loadEstimateDraft';
import { ABC_COLORS, supplierColor } from '@/modules/supply/utils/loadColors';

/** Camión: carga del proveedor activo (o de la consolidación) con sus productos y la restricción logística. */
export function LoadEstimateTruck({ estimate, truck }: { estimate: LoadEstimate; truck: LoadEstimateTruckState }) {
  const { t } = useTranslation();
  const { draftRef, saveProductConfig } = estimate;
  const {
    consolidatedView, activeSource, activeBreakdown, adjustedItems, restoreValidated, colorBy, setColorBy, estimableMarked, fillPct,
    productContext, restriction, lockedProducts, setLockedProducts, lockedProductIds, changeQuantities, roundedKey,
  } = truck;
  // Qué porcentaje del camión ocupa cada clase ABC o cada proveedor: lo informa el propio relleno, para que la
  // leyenda muestre el mismo número que se ve coloreado dentro del camión (misma clave: letra ABC o id de proveedor).
  const [groupFillPct, setGroupFillPct] = useState<Record<string, number>>({});
  const pctLabel = (key: string) => ((groupFillPct[key] ?? 0) > 0 ? `${groupFillPct[key].toFixed(0)}%` : null);

  if (!consolidatedView && !(activeSource && activeBreakdown)) return null;
  return (
        <section className={`${CARD} overflow-hidden`}>
          <div className="border-b border-gray-100 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-gray-900">
                {consolidatedView ? `${t(`${T}.consolidatedTitle`)} · ${consolidatedView.template?.code ?? ''}` : activeSource?.supplierName}
              </h2>
              {adjustedItems.length > 0 && !consolidatedView ? (
                <p className="flex items-center gap-3 text-xs text-gray-600" role="status">
                  {t(`${T}.adjustedCount`, { count: adjustedItems.length })}
                  <button type="button" onClick={restoreValidated} className="font-semibold text-[#1681ba] hover:underline">{t(`${T}.restoreValidated`)}</button>
                </p>
              ) : null}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
              {consolidatedView ? (
                <span role="group" aria-label={t(`${T}.colorBy`)} className="inline-flex items-center gap-1">
                  {t(`${T}.colorBy`)}
                  {(['abc', 'supplier'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      aria-pressed={colorBy === mode}
                      onClick={() => setColorBy(mode)}
                      className={`rounded-md px-2 py-0.5 font-medium ${colorBy === mode ? 'bg-[#1681ba] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    >
                      {t(`${T}.${mode === 'abc' ? 'colorAbc' : 'colorSupplier'}`)}
                    </button>
                  ))}
                </span>
              ) : null}
              {(consolidatedView ? colorBy : 'abc') === 'abc'
                ? ([['A', ABC_COLORS.A], ['B', ABC_COLORS.B], ['C', ABC_COLORS.C]] as const).map(([label, color]) => (
                  <span key={label} className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} aria-hidden="true" />
                    {label}
                    {pctLabel(label) ? <span className="font-medium text-gray-900">{pctLabel(label)}</span> : null}
                  </span>
                ))
                : estimableMarked.map((source) => (
                  <span key={source.supplierId} className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: supplierColor(source.supplierId) }} aria-hidden="true" />
                    {source.supplierName}
                    {pctLabel(String(source.supplierId)) ? <span className="font-medium text-gray-900">{pctLabel(String(source.supplierId))}</span> : null}
                  </span>
                ))}
            </div>
          </div>
          <SupplyLoadVisualization
            result={consolidatedView ? consolidatedView.result : (activeSource as LoadEstimateSupplierResult).result}
            t={t}
            estimateOnly
            compact
            showSupplier={Boolean(consolidatedView)}
            colorMode={consolidatedView ? colorBy : 'abc'}
            fillPct={consolidatedView ? Math.max(consolidatedView.volumePct ?? 0, consolidatedView.weightPct ?? 0) : fillPct}
            onGroupFillPct={setGroupFillPct}
            hideMetrics
            productContext={productContext}
            targetColumn={restriction.targetColumn}
            lockedProducts={lockedProducts}
            onToggleLock={(productId, locked) => setLockedProducts((current) => ({ ...current, [productId]: locked }))}
            productsAction={(
              <div className="w-full">
                <div className="flex flex-wrap items-center justify-end gap-2">
                {restriction.canUndo ? (
                  <button
                    type="button"
                    onClick={() => void restriction.undo()}
                    disabled={restriction.loading}
                    className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {t('supply.supplyAgent.fillToTargetUndo')}
                  </button>
                ) : null}
                <button
                  type="button"
                  aria-expanded={restriction.open}
                  onClick={() => restriction.setOpen((open) => !open)}
                  className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${restriction.open ? 'border-slate-400 bg-white text-slate-900' : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50'}`}
                >
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${restriction.open ? 'rotate-180' : ''}`} aria-hidden="true" />
                  {t('supply.supplyAgent.loadRestrictionButton')}
                </button>
                </div>
                <LoadRestrictionPanel restriction={restriction} lockedCount={lockedProductIds.length} t={t} />
              </div>
            )}
            onSaveProductConfig={saveProductConfig}
            onApplyRoundedBoxes={(items) => changeQuantities(Object.fromEntries(
              items.filter((item) => item.supplierId != null).map((item) => [roundedKey(item), item.quantity]),
            ))}
            onClearRoundedBoxes={(items) => {
              // Vuelve a la cantidad sugerida original que guarda el borrador.
              const original = new Map((draftRef.current?.lines ?? []).map((line) => [line.product_id, num(line.suggested_quantity)]));
              changeQuantities(Object.fromEntries(items
                .filter((item) => item.supplierId != null && original.has(item.productId))
                .map((item) => [roundedKey(item), original.get(item.productId) as number])));
            }}
          />
        </section>
  );
}
